import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

function Settings() {
  const { profile, user, refreshProfile, loading } = useAuth();
  const { language, setLanguage, t, availableLanguages } = useLanguage();

  const [profileForm, setProfileForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    city: "",
  });

  const [addressForm, setAddressForm] = useState({
    flat_house_no: "",
    street_area: "",
    city: "",
    state: "",
    country: "India",
    pincode: "",
  });

  const [emailForm, setEmailForm] = useState({
    newEmail: "",
  });

  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  useEffect(() => {
    if (profile) {
      setProfileForm({
        full_name: profile.full_name || "",
        phone: profile.phone || "",
        email: profile.email || "",
        city: profile.city || "",
      });

      setAddressForm({
        flat_house_no: profile.flat_house_no || "",
        street_area: profile.street_area || "",
        city: profile.city || "",
        state: profile.state || "",
        country: profile.country || "India",
        pincode: profile.pincode || "",
      });

      setEmailForm({
        newEmail: profile.email || "",
      });
    }
  }, [profile]);

  const handleProfileChange = (e) => {
    setProfileForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAddressChange = (e) => {
    setAddressForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleEmailChange = (e) => {
    setEmailForm((prev) => ({
      ...prev,
      newEmail: e.target.value,
    }));
  };

  const saveProfile = async () => {
    try {
      if (!user?.id) return;

      setSavingProfile(true);

      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: profileForm.full_name,
          phone: profileForm.phone,
          city: profileForm.city,
        })
        .eq("id", user.id);

      if (error) throw error;

      await refreshProfile();
      alert("Profile updated successfully.");
    } catch (error) {
      console.error("Save profile error:", error);
      alert(error.message || "Failed to save profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveAddress = async () => {
    try {
      if (!user?.id) return;

      setSavingAddress(true);

      const { error } = await supabase
        .from("profiles")
        .update({
          flat_house_no: addressForm.flat_house_no,
          street_area: addressForm.street_area,
          city: addressForm.city,
          state: addressForm.state,
          country: addressForm.country,
          pincode: addressForm.pincode,
        })
        .eq("id", user.id);

      if (error) throw error;

      await refreshProfile();
      alert("Address updated successfully.");
    } catch (error) {
      console.error("Save address error:", error);
      alert(error.message || "Failed to save address.");
    } finally {
      setSavingAddress(false);
    }
  };

  const updateEmail = async () => {
    try {
      if (!user?.id) return;

      if (!emailForm.newEmail.trim()) {
        throw new Error("Please enter an email.");
      }

      setSavingEmail(true);

      const { error: authError } = await supabase.auth.updateUser({
        email: emailForm.newEmail.trim(),
      });

      if (authError) throw authError;

      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          email: emailForm.newEmail.trim(),
          is_email_verified: false,
        })
        .eq("id", user.id);

      if (profileError) throw profileError;

      await refreshProfile();

      alert(
        "Email update initiated. Please verify the email from your inbox before using it as a login method."
      );
    } catch (error) {
      console.error("Update email error:", error);
      alert(error.message || "Failed to update email.");
    } finally {
      setSavingEmail(false);
    }
  };

  const detectCityFromBrowser = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported on this device/browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        try {
          if (!user?.id) return;

          const { error } = await supabase
            .from("profiles")
            .update({
              last_known_lat: lat,
              last_known_lng: lng,
            })
            .eq("id", user.id);

          if (error) throw error;

          alert(
            "Location captured. City resolution should later be handled by backend reverse geocoding."
          );
          await refreshProfile();
        } catch (error) {
          console.error("Save location error:", error);
          alert(error.message || "Failed to save location.");
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        alert("Unable to capture location permission.");
      }
    );
  };

  if (loading) {
    return (
      <div style={page} className="settings-page">
        <p>{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <div style={page} className="settings-page">
      <div style={heroCard} className="settings-page__hero">
        <h1 style={{ marginTop: 0 }}>{t("settings.title")}</h1>
        <p style={heroText}>{t("settings.subtitle")}</p>
      </div>

      <div style={grid} className="settings-page__grid">
        <div style={card} className="settings-page__card">
          <h2>{t("settings.profileSection")}</h2>

          <label style={label}>{t("settings.fullName")}</label>
          <input
            name="full_name"
            value={profileForm.full_name}
            onChange={handleProfileChange}
            style={input}
          />

          <label style={label}>{t("settings.phone")}</label>
          <input
            name="phone"
            value={profileForm.phone}
            onChange={handleProfileChange}
            style={input}
            disabled
          />

          <label style={label}>{t("settings.city")}</label>
          <input
            name="city"
            value={profileForm.city}
            onChange={handleProfileChange}
            style={input}
          />

          <button onClick={detectCityFromBrowser} style={secondaryBtn}>
            Use Device Location
          </button>

          <button onClick={saveProfile} style={button} disabled={savingProfile}>
            {savingProfile ? "Saving..." : t("settings.saveProfile")}
          </button>
        </div>

        <div style={card} className="settings-page__card">
          <h2>{t("settings.languageSection")}</h2>

          <label style={label}>{t("settings.preferredLanguage")}</label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={input}
          >
            {availableLanguages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </select>

          <div style={box}>
            <div style={smallLabel}>Current Language</div>
            <div style={bigValue}>
              {availableLanguages.find((lang) => lang.code === language)?.label}
            </div>
          </div>
        </div>

        <div style={card} className="settings-page__card">
          <h2>Email Login & Recovery</h2>

          <p style={muted}>
            Add or update email on your auth account. Once verified, it can be
            used for email login and OTP recovery.
          </p>

          <label style={label}>Email</label>
          <input
            value={emailForm.newEmail}
            onChange={handleEmailChange}
            type="email"
            placeholder="Enter your email"
            style={input}
          />

          <div style={box}>
            <div style={smallLabel}>Email verification status</div>
            <div style={bigValue}>
              {profile?.is_email_verified ? "Verified" : "Not verified"}
            </div>
          </div>

          <button onClick={updateEmail} style={button} disabled={savingEmail}>
            {savingEmail ? "Updating..." : "Update Email"}
          </button>
        </div>

        <div style={card} className="settings-page__card">
          <h2>{t("settings.addressSection")}</h2>

          <label style={label}>{t("settings.flatHouseNo")}</label>
          <input
            name="flat_house_no"
            value={addressForm.flat_house_no}
            onChange={handleAddressChange}
            style={input}
          />

          <label style={label}>{t("settings.streetArea")}</label>
          <input
            name="street_area"
            value={addressForm.street_area}
            onChange={handleAddressChange}
            style={input}
          />

          <div style={twoColGrid} className="settings-page__two-col-grid">
            <div>
              <label style={label}>{t("settings.city")}</label>
              <input
                name="city"
                value={addressForm.city}
                onChange={handleAddressChange}
                style={input}
              />
            </div>

            <div>
              <label style={label}>{t("settings.state")}</label>
              <input
                name="state"
                value={addressForm.state}
                onChange={handleAddressChange}
                style={input}
              />
            </div>

            <div>
              <label style={label}>{t("settings.country")}</label>
              <input
                name="country"
                value={addressForm.country}
                onChange={handleAddressChange}
                style={input}
              />
            </div>

            <div>
              <label style={label}>{t("settings.pincode")}</label>
              <input
                name="pincode"
                value={addressForm.pincode}
                onChange={handleAddressChange}
                style={input}
              />
            </div>
          </div>

          <button onClick={saveAddress} style={button} disabled={savingAddress}>
            {savingAddress ? "Saving..." : t("settings.saveAddress")}
          </button>
        </div>

        <div style={card} className="settings-page__card">
          <h2>Password & Security</h2>

          <div style={box}>
            <div style={smallLabel}>Phone verification</div>
            <div style={bigValue}>
              {profile?.is_phone_verified ? "Verified" : "Not verified"}
            </div>
          </div>

          <div style={{ marginTop: "14px" }}>
            <Link to="/change-password" style={linkButton}>
              Change Password
            </Link>
          </div>
        </div>

        <div style={card} className="settings-page__card">
          <h2>{t("settings.accountSection")}</h2>

          <div style={accountGrid}>
            <div style={box}>
              <div style={smallLabel}>{t("settings.role")}</div>
              <div style={bigValue}>{profile?.role || "N/A"}</div>
            </div>

            <div style={box}>
              <div style={smallLabel}>{t("settings.uniqueId")}</div>
              <div style={bigValue}>{profile?.display_user_id || "N/A"}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const page = {
  background: "radial-gradient(circle at top, #101010 0%, #000 55%)",
  minHeight: "100vh",
  padding: "40px",
  color: "#fff",
  fontFamily: "Arial, sans-serif",
  maxWidth: "1100px",
  margin: "0 auto",
};

const heroCard = {
  background: "linear-gradient(135deg, #111, #191919)",
  border: "1px solid #222",
  borderRadius: "22px",
  padding: "28px",
  marginBottom: "24px",
};

const heroText = {
  color: "#d1d1d1",
  lineHeight: "1.8",
  marginBottom: 0,
};

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: "18px",
};

const card = {
  background: "#111",
  padding: "24px",
  borderRadius: "18px",
  border: "1px solid #2a2a2a",
  boxShadow: "0 12px 28px rgba(0,0,0,0.20)",
};

const label = {
  display: "block",
  marginBottom: "8px",
  marginTop: "12px",
  color: "#fff",
};

const input = {
  width: "100%",
  padding: "12px 14px",
  marginBottom: "12px",
  borderRadius: "10px",
  border: "1px solid #2a2a2a",
  background: "#0b0b0b",
  color: "#fff",
  boxSizing: "border-box",
};

const button = {
  padding: "12px 18px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
  marginTop: "8px",
};

const secondaryBtn = {
  padding: "12px 18px",
  border: "1px solid #fff",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
  marginTop: "8px",
  marginRight: "10px",
};

const box = {
  marginTop: "14px",
  background: "#0b0b0b",
  border: "1px solid #222",
  borderRadius: "14px",
  padding: "16px",
};

const smallLabel = {
  color: "#9f9f9f",
  fontSize: "13px",
};

const bigValue = {
  marginTop: "6px",
  color: "#fff",
  fontWeight: "bold",
  wordBreak: "break-word",
};

const twoColGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "12px",
};

const accountGrid = {
  display: "grid",
  gap: "14px",
};

const muted = {
  color: "#d1d1d1",
  lineHeight: "1.7",
};

const linkButton = {
  display: "inline-block",
  padding: "12px 18px",
  borderRadius: "10px",
  background: "#fff",
  color: "#000",
  textDecoration: "none",
  fontWeight: "bold",
};

export default Settings;
